import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { House, Users, CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking" });
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/household/invite/${token}`);
        if (!cancelled) setState(data.valid
          ? { status: "valid", email: data.email, ownerName: data.owner_name, ownerEmail: data.owner_email, registered: data.user_registered }
          : { status: "invalid" });
      } catch { if (!cancelled) setState({ status: "invalid" }); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post(`/household/invite/${token}/accept`);
      setDone(true);
      await refresh();
      setTimeout(() => navigate("/profile"), 1500);
    } catch (e) { toast.error(formatError(e)); } finally { setAccepting(false); }
  };

  // Loading state
  if (state.status === "checking") {
    return (
      <div className="min-h-screen bg-sand-100 grid place-items-center">
        <div className="h-10 w-10 rounded-full border-4 border-terracotta-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (state.status === "invalid") {
    return (
      <Shell>
        <div data-testid="invite-invalid" className="text-center">
          <XCircle weight="duotone" size={64} className="mx-auto text-destructive" />
          <h1 className="mt-5 font-heading text-3xl text-stoneink-900 font-semibold">Invite expired</h1>
          <p className="mt-3 text-stoneink-700">This invite is no longer valid. Ask whoever invited you for a fresh link.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-terracotta-600 hover:text-terracotta-700 font-medium">
            Go home <ArrowRight size={16} />
          </Link>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div data-testid="invite-accepted" className="text-center">
          <CheckCircle weight="duotone" size={64} className="mx-auto text-sage-600" />
          <h1 className="mt-5 font-heading text-3xl text-stoneink-900 font-semibold">You&apos;re in the household.</h1>
          <p className="mt-3 text-stoneink-700">Taking you to your profile…</p>
        </div>
      </Shell>
    );
  }

  // Valid invite - check user state
  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="invite-card" className="text-center">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-terracotta-50 text-terracotta-600 grid place-items-center">
          <Users weight="duotone" size={32} />
        </div>
        <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-terracotta-600">Family invite</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl text-stoneink-900 font-semibold tracking-tight">
          {state.ownerName || "Someone"} invited you
        </h1>
        <p className="mt-3 text-stoneink-700">
          Join <span className="font-medium text-stoneink-900">{state.ownerName || state.ownerEmail}</span>&apos;s Nestled household to share your home inventory together.
        </p>
        <div className="mt-6 rounded-2xl border border-sand-300 bg-sand-100 p-4 text-sm text-stoneink-700">
          Invite for <span className="font-medium text-stoneink-900">{state.email}</span>
        </div>

        {!user || user === false ? (
          <div className="mt-8 space-y-3">
            {state.registered ? (
              <Link
                to={`/login?next=${encodeURIComponent(`/accept-invite/${token}`)}&email=${encodeURIComponent(state.email)}`}
                data-testid="invite-login-link"
                className="block h-12 leading-[3rem] rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium"
              >
                Sign in to accept
              </Link>
            ) : (
              <Link
                to={`/register?next=${encodeURIComponent(`/accept-invite/${token}`)}&email=${encodeURIComponent(state.email)}`}
                data-testid="invite-register-link"
                className="block h-12 leading-[3rem] rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium"
              >
                Create your account
              </Link>
            )}
            <Link to="/login" className="block text-sm text-stoneink-500 hover:text-terracotta-600">
              {state.registered ? "Not your account?" : "Already have a Nestled account? Sign in"}
            </Link>
          </div>
        ) : user.email?.toLowerCase() !== state.email.toLowerCase() ? (
          <div className="mt-8 rounded-2xl bg-terracotta-50 border border-terracotta-100 p-4 text-sm text-terracotta-700" data-testid="invite-wrong-account">
            You&apos;re signed in as <strong>{user.email}</strong>, but this invite is for <strong>{state.email}</strong>.
            <div className="mt-3">
              <Link to="/login" className="font-medium underline-offset-4 hover:underline">Switch accounts →</Link>
            </div>
          </div>
        ) : (
          <button
            data-testid="invite-accept-btn"
            onClick={accept} disabled={accepting}
            className="mt-8 w-full h-12 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium disabled:opacity-60 active:scale-[0.99]"
          >
            {accepting ? "Joining…" : "Join household"}
          </button>
        )}
      </motion.div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-sand-100 flex flex-col">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-sand-100/80 border-b border-sand-300">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-terracotta-500 text-white grid place-items-center shadow-ambient">
              <House weight="duotone" size={22} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold text-stoneink-900 leading-none">Nestled</div>
              <div className="text-xs text-stoneink-500 mt-0.5">a place for everything</div>
            </div>
          </Link>
        </div>
      </header>
      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md rounded-3xl border border-sand-300 bg-white p-8 sm:p-10 shadow-ambient">
          {children}
        </div>
      </main>
    </div>
  );
}
